import { useEffect, useState } from "react";

import { Picker as RNPicker } from "@react-native-picker/picker";
import {
  Platform,
  Pressable,
  StyleProp,
  TextStyle,
  View,
  ViewStyle,
  StyleSheet,
} from "react-native";
import {
  Text,
  TextInput,
  Portal,
  Modal,
  useTheme,
  Button,
} from "react-native-paper";
import { Style, useTailwind } from "tailwind-rn";

type Option = { label: string; value: string };

type Props = {
  title?: string;
  options: Option[];
  selectOption: (option: Option) => void;
  style?: StyleProp<ViewStyle>;
  placeholder?: string;
  disableInputLabel?: boolean;
  preselectedValue?: string | null;
  disabled?: boolean;
};

const emptyOption: Option = { label: "", value: "" };
// TODO: re-rendering modal on deselection
// Wrapper around RNPicker to support all platforms and objects instead of strings with an 'empty' initial selected option
export default function Picker({
  title,
  options,
  style,
  selectOption,
  placeholder,
  disableInputLabel,
  preselectedValue,
  disabled,
}: Props) {
  const [selectedValue, setSelectedValue] = useState(preselectedValue);

  useEffect(() => {
    setSelectedValue(preselectedValue);
  }, [preselectedValue]);

  const selectedOption =
    options.find((o) => o.value === selectedValue) || emptyOption;

  // track value from picker in internal state and update only if its not the empty
  const selectValue = (value: string | undefined) => {
    setSelectedValue(value);

    const opt = options.find((o) => o.value === value);
    selectOption(opt || emptyOption);
  };

  const PlatformPicker = Platform.select({
    ios: IOSPicker,
    android: AndroidPicker,
    default: WebPicker,
  });

  return (
    <View style={style}>
      <PlatformPicker
        title={title}
        selectedOption={selectedOption}
        selectValue={selectValue}
        placeholder={placeholder}
        disableInputLabel={disableInputLabel}
        options={options}
        disabled={disabled}
      />
    </View>
  );
}

type PlatformPickerProps = Pick<
  Props,
  "title" | "options" | "placeholder" | "disableInputLabel" | "disabled"
> & {
  selectValue: (value: string | undefined) => void;
  selectedOption: Option;
};

const AndroidPicker = (props: PlatformPickerProps) => {
  const { colors } = useTheme();
  const tailwind = useTailwind();

  return (
    <View style={tailwind("mb-2")}>
      {props.title && <Text variant="titleMedium">{props.title}</Text>}
      <View
        style={[
          tailwind("mt-1 border rounded-xl pl-2"),
          { borderColor: colors.outline },
        ]}
      >
        <InternalPicker {...props} style={{ color: colors.onSurface }} />
      </View>
    </View>
  );
};

const IOSPicker = (props: PlatformPickerProps) => {
  const { colors } = useTheme();
  const tailwind = useTailwind();

  const [modalVisible, setModalVisible] = useState(false);
  const openModal = () => setModalVisible(true);
  const dismissModal = () => setModalVisible(false);

  const placeholder = props.placeholder || props.title;

  return (
    <View style={tailwind("mt-1")}>
      <Pressable onPress={openModal} disabled={props.disabled}>
        <View pointerEvents="none">
          <TextInput
            mode="outlined"
            right={!props.disabled && <TextInput.Icon icon="chevron-down" />}
            placeholder={placeholder}
            label={props.disableInputLabel ? undefined : props.title}
            value={props.selectedOption.label}
          />
        </View>
      </Pressable>
      <Portal>
        <Modal
          visible={modalVisible}
          onDismiss={dismissModal}
          contentContainerStyle={[
            tailwind("p-6 mx-4 rounded-xl"),
            { backgroundColor: colors.surface },
          ]}
        >
          {props.title && <Text variant="titleMedium">{props.title}</Text>}
          <InternalPicker {...props} />
          <View style={tailwind("flex-row justify-end")}>
            <Button mode="text" onPress={dismissModal}>
              Listo
            </Button>
          </View>
        </Modal>
      </Portal>
    </View>
  );
};

function WebPicker(props: PlatformPickerProps) {
  const tailwind = useTailwind();
  const { colors } = useTheme();
  const style = StyleSheet.flatten([
    tailwind("mt-1 border rounded-xl p-2"),
    { borderColor: colors.outline },
  ]) as Style;

  return (
    <View>
      {props.title && (
        <Text variant="titleMedium" style={tailwind("mb-2")}>
          {props.title}
        </Text>
      )}
      <select
        value={props.selectedOption.value}
        onChange={(e) => props.selectValue(e.target.value)}
        style={style}
      >
        <option value="" label={props.placeholder} disabled />
        {props.options.map(({ label, value }, index) => (
          <option value={value} key={index}>
            {label}
          </option>
        ))}
      </select>
    </View>
  );
}

type InternalPickerProps = PlatformPickerProps & {
  style?: StyleProp<TextStyle>;
};

// shared RNPicker used by IOS and Android
function InternalPicker({
  selectedOption,
  title,
  selectValue,
  options,
  style,
  placeholder,
  disabled,
}: InternalPickerProps) {
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const { colors } = useTheme();

  return (
    <View pointerEvents={disabled ? "none" : undefined}>
      <RNPicker
        selectedValue={selectedOption.value}
        onValueChange={selectValue}
        prompt={title}
        style={style}
        onFocus={() => setShowPlaceholder(false)}
        onBlur={() => setShowPlaceholder(true)}
        // elevation level1 matches card background, we use this to show invisible
        dropdownIconColor={disabled ? colors.elevation.level1 : undefined}
      >
        {/* empty item is necessary so we can select the first real one */}
        <RNPicker.Item
          label={
            Platform.OS === "android" && showPlaceholder ? placeholder : ""
          }
          value=""
          key="placeholder"
        />

        {options.map(({ label, value }, index) => (
          <RNPicker.Item label={label} value={value} key={index} />
        ))}
      </RNPicker>
    </View>
  );
}
